import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ExternalLink, Mail, MapPin } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import RequestQuoteDialog from "@/components/affiliates/RequestQuoteDialog";
import { categoryLabel } from "@/lib/affiliates";

const AffiliateProfile = () => {
  const { slug } = useParams();
  const [open, setOpen] = useState(false);

  const { data: affiliate, isLoading } = useQuery({
    queryKey: ["affiliate", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliates_public" as any)
        .select("*")
        .eq("slug", slug!)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
    enabled: !!slug,
  });

  const { data: publicEmail } = useQuery({
    queryKey: ["affiliate-public-email", slug],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_affiliate_public_email" as any, {
        _slug: slug!,
      });
      if (error) throw error;
      return (data as string | null) ?? null;
    },
    enabled: !!slug && !!affiliate?.show_contact_email_public,
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      <section className="pt-36 pb-10">
        <div className="container mx-auto px-6 max-w-4xl">
          <Link to="/affiliates/directory" className="text-sm text-primary hover:underline inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="h-3 w-3" /> Back to directory
          </Link>

          {isLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : !affiliate ? (
            <div className="text-center py-16">
              <h1 className="font-display text-2xl font-bold mb-2">Provider not found</h1>
              <p className="text-muted-foreground">This affiliate may have been removed or is not yet active.</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col md:flex-row md:items-start gap-6 mb-10">
                <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                  {affiliate.logo_url ? (
                    <img src={affiliate.logo_url} alt={`${affiliate.name} logo`} className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-display text-3xl font-bold text-primary">{affiliate.name.slice(0, 1)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[10px] uppercase tracking-wider text-primary font-semibold">
                    {categoryLabel(affiliate.category)}
                  </span>
                  <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 mb-2">{affiliate.name}</h1>
                  {affiliate.tagline && (
                    <p className="text-lg text-muted-foreground mb-3">{affiliate.tagline}</p>
                  )}
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {affiliate.location && (
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {affiliate.location}</span>
                    )}
                    {affiliate.website && (
                      <a href={affiliate.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-primary">
                        <ExternalLink className="h-3.5 w-3.5" /> Website
                      </a>
                    )}
                    {publicEmail && (
                      <a href={`mailto:${publicEmail}`} className="inline-flex items-center gap-1 hover:text-primary">
                        <Mail className="h-3.5 w-3.5" /> Email
                      </a>
                    )}
                  </div>
                </div>
                <Button onClick={() => setOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
                  Request a Quote
                </Button>
              </div>

              {affiliate.description && (
                <div className="bg-card border border-border rounded-xl p-6 mb-6">
                  <h2 className="font-display text-lg font-semibold mb-3">About</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                    {affiliate.description}
                  </p>
                </div>
              )}

              {affiliate.services?.length > 0 && (
                <div className="bg-card border border-border rounded-xl p-6 mb-6">
                  <h2 className="font-display text-lg font-semibold mb-3">Services</h2>
                  <div className="flex flex-wrap gap-2">
                    {affiliate.services.map((s) => (
                      <span key={s} className="text-xs bg-primary/10 text-primary px-3 py-1 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {affiliate.specialties?.length > 0 && (
                <div className="bg-card border border-border rounded-xl p-6">
                  <h2 className="font-display text-lg font-semibold mb-3">Specialties</h2>
                  <div className="flex flex-wrap gap-2">
                    {affiliate.specialties.map((s) => (
                      <span key={s} className="text-xs bg-muted text-muted-foreground px-3 py-1 rounded-full">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              <RequestQuoteDialog
                open={open}
                onOpenChange={setOpen}
                affiliateId={affiliate.id}
                affiliateName={affiliate.name}
              />
            </>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default AffiliateProfile;